import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, Paper, Typography } from '@mui/material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { useState, useCallback, useMemo, useEffect } from 'react';
import SearchField from 'src/shared/components/form/searchField';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { AddBox, AddCircle, IndeterminateCheckBox } from '@mui/icons-material';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceDeliveryMethodStringEnum } from 'src/shared/models/enums/invoiceDeliveryMethodString.enum';
import { InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { AdvancedSearch } from 'src/shared/components/organisms/advancedSearch';
import { AdvancedSearchChipSummary, ChipSpecs } from 'src/shared/components/organisms/advancedSearchChipSummary';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { CreateMemberInvoiceStepperModal } from '../components/createMemberInvoiceStepperModal';
import { CreateInvoiceModal } from '../components/createInvoiceModal';
import { InvoiceSearchFilter } from '../models/invoiceSearchFilter';
import { InvoiceGroupsAndMissions } from '../components/invoiceGroupsAndMissions';
import InvoiceTabs, { invoiceTabs } from '../components/invoiceTabs';
import InvoiceDataGrid from '../components/invoiceDataGrid';

export const InvoiceOverviewPage = () => {
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const { currentUser } = useGlobals();
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState<boolean>(false);
  const [showCreateMemberInvoiceModal, setShowCreateMemberInvoiceModal] = useState<boolean>(false);
  const [invoiceSearchFilter, setInvoiceSearchFilter] = useLocalStorage(LocalStorageKeys.InvoiceSearchFilter, {
    status: invoiceTabs.filter((x) => x.key === 'UnPaid')[0],
  } as unknown as InvoiceSearchFilter);

  const hasAdvancedFilters = !!(
    invoiceSearchFilter?.deliveryMethod ||
    invoiceSearchFilter?.missionType ||
    invoiceSearchFilter?.sendingType ||
    invoiceSearchFilter?.createdDateFrom ||
    invoiceSearchFilter?.createdDateTo
  );
  const [formExpanded, setFormExpanded] = useLocalStorage<boolean>('invoiceSearchFormExpanded', hasAdvancedFilters);

  const sysProducts = getSysProducts(currentUser);
  const hasMemberReg = sysProducts.some((x) => x.yvName.toLowerCase() === SysProductYvNameEnum.MemberReg.toLowerCase());

  type DeliveryMethodOption = { value: string; label: string };
  const deliveryMethodOptions: DeliveryMethodOption[] = Object.values(InvoiceDeliveryMethodStringEnum)
    .filter(
      (v) =>
        v !== InvoiceDeliveryMethodStringEnum.Unknown &&
        v !== InvoiceDeliveryMethodStringEnum.SMS &&
        v !== InvoiceDeliveryMethodStringEnum.File &&
        v !== InvoiceDeliveryMethodStringEnum.EBrev,
    )
    .map((v) => ({ value: v, label: t(`invoice:deliveryMethod.${v}`) }));

  type SendingTypeOption = { value: InvoiceSendingTypeIntegerEnum; label: string };
  const sendingTypeOptions: SendingTypeOption[] = useMemo(
    () => [
      { value: InvoiceSendingTypeIntegerEnum.Main, label: t('invoice:dashboard.sendingType.main') },
      { value: InvoiceSendingTypeIntegerEnum.Reminder1, label: t('invoice:dashboard.sendingType.reminder1') },
      { value: InvoiceSendingTypeIntegerEnum.Reminder2, label: t('invoice:dashboard.sendingType.reminder2') },
    ],
    [t],
  );

  const searchFilter: InvoiceSearchFilter = useMemo(() => {
    return {
      ownerId: invoiceSearchFilter?.ownerId,
      invoiceMissionId: invoiceSearchFilter?.invoiceMissionId,
      searchString: invoiceSearchFilter?.searchString,
      status: invoiceSearchFilter?.status,
      deliveryMethod: invoiceSearchFilter?.deliveryMethod,
      missionType: invoiceSearchFilter?.missionType,
      sendingType: invoiceSearchFilter?.sendingType,
      createdDateFrom: invoiceSearchFilter?.createdDateFrom,
      createdDateTo: invoiceSearchFilter?.createdDateTo,
      paidDateFrom: invoiceSearchFilter?.paidDateFrom,
      paidDateTo: invoiceSearchFilter?.paidDateTo,
      pageIndex: invoiceSearchFilter?.pageIndex ?? DEFAULT_PAGE_INDEX,
      pageSize: invoiceSearchFilter?.pageSize ?? DEFAULT_PAGE_SIZE,
    } as unknown as InvoiceSearchFilter;
  }, [
    invoiceSearchFilter?.invoiceMissionId,
    invoiceSearchFilter?.ownerId,
    invoiceSearchFilter?.pageIndex,
    invoiceSearchFilter?.pageSize,
    invoiceSearchFilter?.searchString,
    invoiceSearchFilter?.status,
    invoiceSearchFilter?.deliveryMethod,
    invoiceSearchFilter?.missionType,
    invoiceSearchFilter?.sendingType,
    invoiceSearchFilter?.createdDateFrom,
    invoiceSearchFilter?.createdDateTo,
    invoiceSearchFilter?.paidDateFrom,
    invoiceSearchFilter?.paidDateTo,
  ]);

  const loadingCallback = useCallback(
    (isLoading: boolean) => {
      if (isMounted()) {
        setLoading(isLoading);
      }
    },
    [isMounted],
  );

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: tempSearchString } }));
      } else {
        setInvoiceSearchFilter((prev) => ({ ...prev, ...{ searchString: '' } }));
      }
    }
  }, [isMounted, searchInitialized, setInvoiceSearchFilter, tempSearchString]);

  const handleResetAdvanced = () => {
    setInvoiceSearchFilter((prev) => ({
      ...prev,
      deliveryMethod: undefined,
      missionType: undefined,
      sendingType: undefined,
      createdDateFrom: undefined,
      createdDateTo: undefined,
      paidDateFrom: undefined,
      paidDateTo: undefined,
      pageIndex: 0,
    }));
  };

  type ChipField = 'createdDateFrom' | 'createdDateTo' | 'paidDateFrom' | 'paidDateTo' | 'deliveryMethod' | 'missionType' | 'sendingType';

  const chips: ChipSpecs<ChipField>[] = useMemo(() => {
    const list: ChipSpecs<ChipField>[] = [];
    if (invoiceSearchFilter?.createdDateFrom) {
      list.push({ field: 'createdDateFrom', title: t('invoice:createdDateFrom'), label: invoiceSearchFilter.createdDateFrom });
    }
    if (invoiceSearchFilter?.createdDateTo) {
      list.push({ field: 'createdDateTo', title: t('invoice:createdDateTo'), label: invoiceSearchFilter.createdDateTo });
    }
    if (invoiceSearchFilter?.paidDateFrom) {
      list.push({ field: 'paidDateFrom', title: t('invoice:paidDateFrom'), label: invoiceSearchFilter.paidDateFrom });
    }
    if (invoiceSearchFilter?.paidDateTo) {
      list.push({ field: 'paidDateTo', title: t('invoice:paidDateTo'), label: invoiceSearchFilter.paidDateTo });
    }
    if (invoiceSearchFilter?.deliveryMethod) {
      const opt = deliveryMethodOptions.find((x) => x.value === invoiceSearchFilter.deliveryMethod);
      list.push({ field: 'deliveryMethod', title: t('invoice:deliveryMethod.label'), label: opt?.label || invoiceSearchFilter.deliveryMethod });
    }
    if (invoiceSearchFilter?.missionType?.length) {
      list.push({ field: 'missionType', title: t('invoice:InvoiceType'), label: t('invoice:onlyMemberInvoices') });
    }
    if (invoiceSearchFilter?.sendingType) {
      const opt = sendingTypeOptions.find((x) => x.value === invoiceSearchFilter.sendingType);
      list.push({ field: 'sendingType', title: t('invoice:sendingType'), label: opt?.label || '' });
    }
    return list;
  }, [
    invoiceSearchFilter?.createdDateFrom,
    invoiceSearchFilter?.createdDateTo,
    invoiceSearchFilter?.paidDateFrom,
    invoiceSearchFilter?.paidDateTo,
    invoiceSearchFilter?.deliveryMethod,
    invoiceSearchFilter?.missionType,
    invoiceSearchFilter?.sendingType,
    deliveryMethodOptions,
    sendingTypeOptions,
    t,
  ]);

  const handleRemoveChip = (field: ChipField) => {
    setInvoiceSearchFilter((prev) => ({ ...prev, [field]: undefined, pageIndex: 0 }));
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs={12} md={4}>
          <MainContentHeader title={t('invoice:Invoices')} />
        </Grid>
        <Grid item xs={12} md={8} style={{ display: 'flex', justifyContent: 'flex-end', gridColumnGap: '5px' }}>
          {hasMemberReg && (
            <Button
              onClick={() => setShowCreateMemberInvoiceModal(true)}
              variant='contained'
              color='primary'
              startIcon={<AddCircle fontSize='small' />}
              disableElevation
            >
              {t('invoice:createMemberInvoice.createMemberInvoiceButton')}
            </Button>
          )}
          <Button onClick={() => setShowCreateInvoiceModal(true)} variant='contained' color='primary' startIcon={<AddCircle />} disableElevation>
            {t('invoice:createInvoice.createInvoiceButton')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <PageFilterSection
              leftContent={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchField
                    initialValue={searchFilter.searchString || ''}
                    onSearch={(newSearchString) => {
                      setSearchInitialized(true);
                      setTempSearchString(newSearchString);
                    }}
                  />
                  <Button onClick={() => setFormExpanded((prev) => !prev)} sx={{ justifyContent: 'left', display: 'flex', whiteSpace: 'nowrap' }}>
                    <Typography variant='body2' sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {!formExpanded && <AddBox fontSize='small' />}
                      {formExpanded && <IndeterminateCheckBox fontSize='small' />}
                      {formExpanded ? t('common:button.hideForm') : t('common:button.showForm')}
                    </Typography>
                  </Button>
                </Box>
              }
              rightContent={
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <InvoiceGroupsAndMissions
                    callback={(ownId, missionId) => {
                      setInvoiceSearchFilter((prev) => ({ ...prev, ...{ invoiceMissionId: missionId, ownerId: ownId } }));
                    }}
                    initialInvoiceGroupId={searchFilter.ownerId}
                    initialInvoiceMissionId={searchFilter.invoiceMissionId}
                  />
                </Box>
              }
            />
            <Grid item xs={12} mx={2} mt={2} mb={formExpanded ? 0 : 2}>
              <AdvancedSearchChipSummary<ChipField> chips={chips} onRemove={handleRemoveChip} onClearAll={handleResetAdvanced} />
            </Grid>
            <AdvancedSearch
              formExpanded={formExpanded}
              title={t('invoice:advancedSearch')}
              resetCallback={handleResetAdvanced}
              showButtonActions={false}
              content={
                <Box mt={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <DatePicker
                        label={t('invoice:createdDateFrom')}
                        value={invoiceSearchFilter?.createdDateFrom ? dayjs(invoiceSearchFilter.createdDateFrom) : null}
                        onChange={(val) => {
                          if (val && (!val.isValid() || val.year() < 1900)) return;
                          setInvoiceSearchFilter((prev) => ({ ...prev, createdDateFrom: val?.format('YYYY-MM-DD') || undefined, pageIndex: 0 }));
                        }}
                        slotProps={{
                          textField: { size: 'small', fullWidth: true, sx: { backgroundColor: 'white' }, InputLabelProps: { shrink: true } },
                          field: { clearable: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <DatePicker
                        label={t('invoice:createdDateTo')}
                        value={invoiceSearchFilter?.createdDateTo ? dayjs(invoiceSearchFilter.createdDateTo) : null}
                        onChange={(val) => {
                          if (val && (!val.isValid() || val.year() < 1900)) return;
                          setInvoiceSearchFilter((prev) => ({ ...prev, createdDateTo: val?.format('YYYY-MM-DD') || undefined, pageIndex: 0 }));
                        }}
                        slotProps={{
                          textField: { size: 'small', fullWidth: true, sx: { backgroundColor: 'white' }, InputLabelProps: { shrink: true } },
                          field: { clearable: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <DatePicker
                        label={t('invoice:paidDateFrom')}
                        value={invoiceSearchFilter?.paidDateFrom ? dayjs(invoiceSearchFilter.paidDateFrom) : null}
                        onChange={(val) => {
                          if (val && (!val.isValid() || val.year() < 1900)) return;
                          setInvoiceSearchFilter((prev) => ({ ...prev, paidDateFrom: val?.format('YYYY-MM-DD') || undefined, pageIndex: 0 }));
                        }}
                        slotProps={{
                          textField: { size: 'small', fullWidth: true, sx: { backgroundColor: 'white' }, InputLabelProps: { shrink: true } },
                          field: { clearable: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <DatePicker
                        label={t('invoice:paidDateTo')}
                        value={invoiceSearchFilter?.paidDateTo ? dayjs(invoiceSearchFilter.paidDateTo) : null}
                        onChange={(val) => {
                          if (val && (!val.isValid() || val.year() < 1900)) return;
                          setInvoiceSearchFilter((prev) => ({ ...prev, paidDateTo: val?.format('YYYY-MM-DD') || undefined, pageIndex: 0 }));
                        }}
                        slotProps={{
                          textField: { size: 'small', fullWidth: true, sx: { backgroundColor: 'white' }, InputLabelProps: { shrink: true } },
                          field: { clearable: true },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{ '& .MuiAutocomplete-root': { backgroundColor: 'white' } }}>
                      <AutocompleteDropdown<DeliveryMethodOption>
                        options={deliveryMethodOptions}
                        nameProp='label'
                        keyProp='value'
                        name='deliveryMethod'
                        value={
                          invoiceSearchFilter?.deliveryMethod
                            ? deliveryMethodOptions.find((x) => x.value === invoiceSearchFilter.deliveryMethod)
                            : undefined
                        }
                        label={t('invoice:deliveryMethod.label')}
                        callback={(val) => setInvoiceSearchFilter((prev) => ({ ...prev, deliveryMethod: val?.value || undefined, pageIndex: 0 }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{ '& .MuiAutocomplete-root': { backgroundColor: 'white' } }}>
                      <AutocompleteDropdown<SendingTypeOption>
                        options={sendingTypeOptions}
                        nameProp='label'
                        keyProp='value'
                        name='sendingType'
                        value={
                          invoiceSearchFilter?.sendingType ? sendingTypeOptions.find((x) => x.value === invoiceSearchFilter.sendingType) : undefined
                        }
                        label={t('invoice:sendingType')}
                        callback={(val) => setInvoiceSearchFilter((prev) => ({ ...prev, sendingType: val?.value || undefined, pageIndex: 0 }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!invoiceSearchFilter?.missionType?.length}
                            onChange={(e) => {
                              setInvoiceSearchFilter((prev) => ({
                                ...prev,
                                missionType: e.target.checked ? [InvoiceMissionTypeTextEnum.member] : undefined,
                                pageIndex: 0,
                              }));
                            }}
                          />
                        }
                        label={t('invoice:onlyMemberInvoices')}
                      />
                    </Grid>
                  </Grid>
                </Box>
              }
            />
            <InvoiceTabs
              initialValue={searchFilter.status || undefined}
              disableAll={loading}
              callback={(val) => setInvoiceSearchFilter((prev) => ({ ...prev, ...{ status: val, pageIndex: 0 } }))}
            />
            <Divider />
            <InvoiceDataGrid
              loadingCallback={loadingCallback}
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) => {
                setInvoiceSearchFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }));
              }}
            />
          </Paper>
        </Grid>
      </GridWrapper>
      <CreateMemberInvoiceStepperModal modalOpen={showCreateMemberInvoiceModal} onClose={() => setShowCreateMemberInvoiceModal(false)} />
      <CreateInvoiceModal
        modalOpen={showCreateInvoiceModal}
        onClose={() => setShowCreateInvoiceModal(false)}
        onSave={() => setShowCreateInvoiceModal(false)}
      />
    </>
  );
};
